import React from 'react';
import { Formik } from 'formik';
import { Col, DatePicker, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Select, Radio } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Yup from '../../../../vendor/yup';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import { DATE_FORMAT } from '../../../../const/system';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import mkTemplatesOptions from '../../../Content/utils/mkTemplatesOptions';
import { NEWS_TEMPLATE_CONTENT_TYPE } from '../../../Content/forms/FormCreateDynamicNews/FormCreateDynamicNews.const';
import { PUSH_NOTIFICATIONS_CONTENT_TYPE } from '../../../Other/forms/FormCreatePushNotification/FormCreatePushNotification.const';
import { useGetTemplatesQuery } from '../../api/templatesApiSlice';



const validationSchema = Yup.lazy((values) => Yup.object().shape({
  id: Yup.string(),
  title: Yup.string().max(255, 'Maximum 255 symbols required'),
  template: Yup.string(),
  created_from: Yup.string().test(
    'test_date_after',
    'Created from date must be same or before than created to',
    (value) => {
      if (!isEmpty(values?.created_to) && !isEmpty(value)) {
        return moment(values?.created_to).isSameOrAfter(value);
      }
      return true;
    },
  ),
  created_to: Yup.string().test(
    'test_date_before',
    'Created from date must be same or after than created to',
    (value) => {
      if (!isEmpty(values?.created_from) && !isEmpty(value)) {
        return moment(values?.created_from).isSameOrBefore(value);
      }
      return true;
    },
  ),
}));

const FormFilterTemplatesVisualizations = ({
  initialValues,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { onBlur } = useDatepickerHandler();

  const { data: { data: templates } = { data: [] }, isFetching: isFetchingTemplates } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', `${NEWS_TEMPLATE_CONTENT_TYPE},${PUSH_NOTIFICATIONS_CONTENT_TYPE}`),
  });


  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-title' })}
              name='title'
            >
              <Input
                name="title"
                placeholder={intl.formatMessage({ id: 'ui-general-specify-title' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]}>
              <FormItem
                label={intl.formatMessage({ id: 'ui-general-template' })}
                name='template'
              >
                <Select
                  allowClear
                  showSearch
                  loading={isFetchingTemplates}
                  name="template"
                  value={values?.template?.toString() ?? null}
                  options={mkTemplatesOptions(templates)}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                  filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                />
              </FormItem>
            </PrivateComponent>

            <Row gutter={[ 16, 16 ]}>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-created-from' })}
                  name='created_from'
                >
                  <DatePicker
                    name='created_from'
                    format={DATE_FORMAT}
                    showToday
                    value={values?.created_from ? moment(values.created_from) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_from', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_from', true);
                      await onBlur('created_from', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>

              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-created-to' })}
                  name='created_to'
                >
                  <DatePicker
                    name='created_to'
                    format={DATE_FORMAT}
                    value={values?.created_to ? moment(values.created_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_to', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_to', true);
                      await onBlur('created_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-used-as-default' })}
                  name='is_default'
                >
                  <Radio.Group
                    size='small'
                    name='is_default'
                    defaultValue={null}
                  >
                    <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormFilterTemplatesVisualizations.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormFilterTemplatesVisualizations;
